import { Award } from "lucide-react";
import Counter from "./Counter";
import Testimonials from "./Testimonials";
import { MaskLines, Reveal } from "./Motion";
import { METRICS, SNAPSHOTS, PARTNERS_GLOBAL, PARTNERS_INDIA, IMG } from "../../data/content";

const Partners = ({ title, items }) => (
  <div>
    <p className="eyebrow text-gold-ink">{title}</p>
    <ul className="mt-5 flex flex-wrap gap-x-8 gap-y-3">
      {items.map((p) => (
        <li key={p} className="font-display text-2xl font-medium text-forest transition-colors hover:text-gold-ink lg:text-3xl">{p}</li>
      ))}
    </ul>
  </div>
);

export default function Proof() {
  return (
    <section id="proof" className="relative bg-white py-24 text-forest lg:py-36" data-testid="proof-section">
      <div className="wrap">
        <p className="eyebrow text-gold-ink">Chapter 06 — Global Footprint</p>
        <MaskLines className="mt-6 font-display text-5xl font-semibold leading-[0.98] sm:text-6xl lg:text-7xl" lines={["Poured from Leeds", <span key="b" className="italic">to Mumbai.</span>]} />

        <div className="mt-14 grid grid-cols-1 border-t border-forest/15 sm:grid-cols-2 lg:grid-cols-4">
          {METRICS.map((m, i) => (
            <Reveal key={m.label} delay={i * 0.08} className="border-b border-forest/15 py-8 sm:pr-6 lg:border-b-0 lg:border-r lg:px-6 lg:first:pl-0 lg:last:border-r-0">
              <Counter value={m.value} suffix={m.suffix} className="font-mono text-5xl font-medium tracking-tighter lg:text-6xl" testId={`metric-${i}`} />
              <p className="mt-3 font-display text-xl font-semibold">{m.label}</p>
              <p className="mt-1 text-sm text-forest-deep">{m.sub}</p>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-8 flex flex-col gap-5 rounded-[1.25rem] bg-mist p-6 sm:flex-row sm:items-center">
          <img src={IMG("award.jpg")} alt="Great Lakes Cider Competition award medal" className="h-16 w-16 rounded-full object-cover" />
          <div className="flex-1">
            <p className="flex items-center gap-2 font-display text-2xl font-semibold"><Award className="h-5 w-5 text-gold-ink" />Best Cider Award, Great Lakes Cider Competition, USA</p>
            <p className="mt-1 text-sm text-forest-deep">Plus £200,000+ in international sales order value and an estimated 5,000+ glasses poured in India since September 2025.</p>
          </div>
        </Reveal>
      </div>

      <Testimonials />

      <div className="wrap">
        <p className="eyebrow text-gold-ink">Snapshots of our journey so far</p>
        <div className="mt-6 grid auto-rows-[150px] grid-cols-2 gap-3 sm:auto-rows-[180px] lg:grid-cols-4" data-testid="snapshots-bento">
          <Reveal className="group relative col-span-2 row-span-2 overflow-hidden rounded-[1.25rem]">
            <img src={IMG("crowd.jpg")} alt="Festival crowd raising Himachal Cider" className="h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest/85 to-transparent" />
            <p className="absolute bottom-5 left-5 font-display text-3xl font-semibold italic text-white">JAI <span className="font-deva not-italic">हो!</span>, everywhere.</p>
          </Reveal>
          {SNAPSHOTS.map((s, i) => (
            <Reveal key={s.city} delay={(i % 4) * 0.05} className="group relative overflow-hidden rounded-[1.25rem]" data-testid={`snapshot-${s.city.toLowerCase()}`}>
              <img src={IMG(s.img)} alt={`Himachal Cider in ${s.city}`} className="h-full w-full object-cover grayscale-[35%] transition-[transform,filter] duration-700 group-hover:scale-105 group-hover:grayscale-0" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/80 via-transparent to-transparent" />
              <p className="absolute bottom-3 left-4 font-mono text-[12px] uppercase tracking-[0.2em] text-white">{s.city}</p>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 grid grid-cols-1 gap-12 border-t border-forest/15 pt-12 lg:grid-cols-2" data-testid="partners-wall">
          <Partners title="Past clients & venues · UK & beyond" items={PARTNERS_GLOBAL} />
          <Partners title="Associations & Events · India" items={PARTNERS_INDIA} />
        </div>
      </div>
    </section>
  );
}
